'use client';

import Image from 'next/image';
import { useId, useMemo, useState } from 'react';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import {
  AlertTriangle,
  BadgeCheck,
  Check,
  CircleDollarSign,
  Clipboard,
  Download,
  DraftingCompass,
  Gauge,
  Mail,
  PackageCheck,
  Ruler,
  Settings2,
  ShieldCheck,
  Sparkles,
  TimerReset,
  UserRound,
} from 'lucide-react';
import {
  FINISHES,
  HANDLE_DEPTH_CONTROL_MAX_MM,
  MACHINES,
  MATERIALS,
  PRODUCTS,
  getMachine,
  getMaterial,
  getProductPreviewConfig,
  getProductSelectionPreset,
  getProduct,
} from './configurator-data';
import { analyzeTcnCompatibility, buildTcnArtifact } from './configurator-tcn';
import {
  ConfiguratorPreview3D,
  GeneratedDoorPreview,
  type ConfiguratorPreviewMode,
} from './configurator-preview-3d';
import { createHandleProfile } from './configurator-handle-profile';
import {
  getConfiguratorMeasurement,
  isConfiguratorMeasurementAvailable,
  type ConfiguratorMeasurement,
  type ConfiguratorMeasurementId,
} from './configurator-measurements';
import {
  buildHandleTransitionCurve,
  buildConfigurationFingerprint,
  deriveDoorGeometry,
  getDoorFieldLimits,
  sampleHandleTransition,
  validateDoorConfig,
} from './door-model';
import { createOutputArtifact, isOutputCurrent } from './configurator-workflow';
import type {
  ConfiguratorState,
  ConfiguratorUser,
  GeneratedOutputs,
  PriceEstimate,
} from './configurator-types';
import styles from './configurator.module.css';

type UpdateConfig = (patch: Partial<ConfiguratorState>) => void;

export function ProductStep({
  config,
  update,
}: {
  config: ConfiguratorState;
  update: UpdateConfig;
}) {
  return (
    <StepFrame
      eyebrow="01 / Výrobok"
      title="Vyberte výrobok"
      description="Vyberte výrobok, ktorý chcete nakonfigurovať. Dostupné výrobky a ich parametre sa budú načítavať z konfiguračných súborov na backende."
    >
      <div className={styles.productGrid}>
        {PRODUCTS.map((product, index) => {
          const selected = config.productId === product.id;
          const previewConfig = selected ? config : getProductPreviewConfig(product.id);
          return (
            <button
              key={product.id}
              type="button"
              disabled={product.status === 'soon'}
              aria-pressed={selected}
              onClick={() => {
                if (!selected) update(getProductSelectionPreset(config, product.id));
              }}
              className={selected ? styles.productCardSelected : styles.productCard}
            >
              <div className={styles.productVisual}>
                <GeneratedDoorPreview
                  config={previewConfig}
                  presentation="card"
                  deferMs={selected ? 0 : (index + 1) * 90}
                  className={styles.productGeneratedPreview}
                />
                {selected && (
                  <span className={styles.selectedTick} aria-hidden="true">
                    <Check size={16} />
                  </span>
                )}
              </div>
              <div className={styles.productCardBody}>
                <h3>{product.label}</h3>
                <p>{product.subtitle}</p>
              </div>
            </button>
          );
        })}
      </div>
    </StepFrame>
  );
}

export function MachineStep({
  config,
  update,
}: {
  config: ConfiguratorState;
  update: UpdateConfig;
}) {
  return (
    <StepFrame
      eyebrow="02 / Technológia"
      title="Vyberte výrobný stroj"
      description="Zatiaľ je pripravený jeden všeobecný profil stroja. Konkrétny model a jeho technické údaje doplníme po potvrdení klientom."
    >
      <div className={styles.machineGrid}>
        {MACHINES.map(machine => {
          const selected = config.machineId === machine.id;
          return (
            <button
              key={machine.id}
              type="button"
              aria-pressed={selected}
              onClick={() => update({ machineId: machine.id })}
              className={selected ? styles.machineCardSelected : styles.machineCard}
            >
              <div className={styles.machinePhoto}>
                {machine.imageSrc ? (
                  <Image
                    src={machine.imageSrc}
                    alt={machine.imageAlt}
                    fill
                    sizes="(max-width: 760px) 100vw, 44vw"
                    className={styles.machinePhotoImage}
                  />
                ) : (
                  <div className={styles.machinePhotoPlaceholder}>
                    <Settings2 size={34} strokeWidth={1.6} />
                    <span>Fotografia stroja bude doplnená</span>
                    <small>Čakáme na presný model od klienta</small>
                  </div>
                )}
                {selected && (
                  <span className={styles.selectedTick}>
                    <Check size={16} />
                  </span>
                )}
              </div>
              <div className={styles.machineCardBody}>
                <h3>{machine.label}</h3>
                <p>{machine.description}</p>
              </div>
            </button>
          );
        })}
      </div>
    </StepFrame>
  );
}

export function ConfigurationStep({
  config,
  update,
  estimate,
}: {
  config: ConfiguratorState;
  update: UpdateConfig;
  estimate: PriceEstimate;
}) {
  const { t } = useTranslations();
  const [requestedMeasurement, setRequestedMeasurement] =
    useState<ConfiguratorMeasurementId>('handle-length');
  const [previewMode, setPreviewMode] = useState<ConfiguratorPreviewMode>('model');
  const material = getMaterial(config.materialId);
  const geometry = deriveDoorGeometry(config);
  const activeMeasurement = isConfiguratorMeasurementAvailable(config, requestedMeasurement)
    ? requestedMeasurement
    : 'handle-length';
  const limits = getDoorFieldLimits(config);
  const validation = config.includeTcnFile
    ? analyzeTcnCompatibility(config).issues
    : validateDoorConfig(config);
  const errors = validation.filter(issue => issue.level === 'error');
  const firstCncPassDepthMm = Number(
    (config.handle.depthMm - config.handle.roughingAllowanceMm).toFixed(1),
  );
  const firstCncPassMinMm = Number(
    (config.handle.depthMm - limits.handle.roughingAllowanceMm.max).toFixed(1),
  );
  const firstCncPassMaxMm = Number(
    (config.handle.depthMm - limits.handle.roughingAllowanceMm.min).toFixed(1),
  );

  const updateHandle = (patch: Partial<ConfiguratorState['handle']>) =>
    update({ handle: { ...config.handle, ...patch } });
  const updateEdges = (patch: Partial<ConfiguratorState['edgeTreatment']>) =>
    update({ edgeTreatment: { ...config.edgeTreatment, ...patch } });
  const updateStock = (patch: Partial<ConfiguratorState['stockAllowanceMm']>) =>
    update({ stockAllowanceMm: { ...config.stockAllowanceMm, ...patch } });
  const activateMeasurement = (id: ConfiguratorMeasurementId) => {
    setRequestedMeasurement(id);
  };
  const activationProps = (id: ConfiguratorMeasurementId) => ({
    active: activeMeasurement === id,
    onActivate: () => activateMeasurement(id),
  });
  const measurementProps = (id: ConfiguratorMeasurementId) => ({
    ...activationProps(id),
    measurement: getConfiguratorMeasurement(config, id),
  });

  return (
    <div className={styles.configurationStep}>
      <div className={styles.configControls}>
        <div className={styles.controlIntro}>
          <span className={styles.eyebrow}>03 / Konfigurácia</span>
          <h2>Navrhnite diel</h2>
          <p>Rozmery a tvar sa premietajú do 3D; technologické CNC údaje sú označené samostatne.</p>
        </div>

        <section className={styles.controlSection}>
          <div className={styles.sectionHeading}>
            <div>
              <span>{t('text4065')}</span>
              <small>Vzhľad náhľadu a kalkulácia materiálu</small>
            </div>
            <span className={styles.sectionNumber}>01</span>
          </div>
          <div className={styles.materialGrid}>
            {MATERIALS.map(option => {
              const selected = config.materialId === option.id;
              return (
                <button
                  type="button"
                  key={option.id}
                  className={selected ? styles.materialSelected : styles.materialButton}
                  aria-pressed={selected}
                  onClick={() =>
                    update({
                      materialId: option.id,
                      finishId: option.id === 'wood' ? 'oil' : 'technical',
                    })
                  }
                >
                  <i
                    style={{
                      background: `linear-gradient(135deg, ${option.color}, ${option.sideColor})`,
                    }}
                  />
                  <span>{option.label}</span>
                  {selected && <Check size={14} />}
                </button>
              );
            })}
          </div>
          <div className={styles.materialMeta}>
            <span>{material.density}</span>
            <span>{material.finish}</span>
          </div>
        </section>

        <section className={styles.controlSection}>
          <div className={styles.sectionHeading}>
            <div>
              <span>Rozmery dielu</span>
              <small>Všetky hodnoty sú v milimetroch</small>
            </div>
            <span className={styles.sectionNumber}>02</span>
          </div>
          <div className={styles.dimensionRangeList}>
            <RangeField
              {...activationProps('finished-width')}
              label="Šírka"
              value={config.widthMm}
              min={limits.widthMm.min}
              max={limits.widthMm.max}
              step={1}
              onChange={widthMm => update({ widthMm })}
            />
            <RangeField
              {...activationProps('finished-height')}
              label="Výška"
              value={config.heightMm}
              min={limits.heightMm.min}
              max={limits.heightMm.max}
              step={1}
              onChange={heightMm => update({ heightMm })}
            />
            <RangeField
              {...activationProps('finished-thickness')}
              label="Hrúbka"
              value={config.thicknessMm}
              min={limits.thicknessMm.min}
              max={limits.thicknessMm.max}
              step={0.5}
              onChange={thicknessMm => update({ thicknessMm })}
            />
            <RangeField
              label="Počet"
              value={config.quantity}
              min={limits.quantity.min}
              max={limits.quantity.max}
              step={1}
              unit="ks"
              onChange={quantity => update({ quantity })}
            />
          </div>
        </section>

        <section className={styles.controlSection}>
          <div className={styles.sectionHeading}>
            <div>
              <span>Integrovaná úchytka</span>
              <small>Hlboký profilový úchyt frézovaný pri hornej hrane</small>
            </div>
            <span className={styles.sectionNumber}>03</span>
          </div>
          {geometry.variant === 'edge-terminated' && (
            <div className={styles.segmentedSmall}>
              <button
                type="button"
                className={
                  config.handle.openSide === 'left' ? styles.segmentActive : styles.segment
                }
                aria-pressed={config.handle.openSide === 'left'}
                onClick={() => updateHandle({ openSide: 'left' })}
              >
                Otvorený bok vľavo
              </button>
              <button
                type="button"
                className={
                  config.handle.openSide === 'right' ? styles.segmentActive : styles.segment
                }
                aria-pressed={config.handle.openSide === 'right'}
                onClick={() => updateHandle({ openSide: 'right' })}
              >
                Otvorený bok vpravo
              </button>
            </div>
          )}

          {geometry.variant !== 'full-edge' && (
            <RangeField
              {...activationProps('handle-length')}
              label="Dĺžka úchytu"
              value={config.handle.lengthMm}
              min={limits.handle.lengthMm.min}
              max={limits.handle.lengthMm.max}
              step={1}
              onChange={lengthMm => updateHandle({ lengthMm })}
            />
          )}
          {geometry.variant === 'double-terminated' && (
            <RangeField
              {...activationProps('handle-offset')}
              label="Odsadenie zľava"
              value={config.handle.offsetMm}
              min={limits.handle.offsetMm.min}
              max={limits.handle.offsetMm.max}
              step={1}
              onChange={offsetMm => updateHandle({ offsetMm })}
            />
          )}
          <RangeField
            {...activationProps('handle-face-width')}
            label="Výška úchytového pásu"
            value={config.handle.faceWidthMm}
            min={limits.handle.faceWidthMm.min}
            max={limits.handle.faceWidthMm.max}
            step={1}
            onChange={faceWidthMm => updateHandle({ faceWidthMm })}
          />
          <RangeField
            {...activationProps('handle-depth')}
            label="Hĺbka frézovania Q"
            value={config.handle.depthMm}
            min={limits.handle.depthMm.min}
            max={Math.min(HANDLE_DEPTH_CONTROL_MAX_MM, limits.handle.depthMm.max)}
            step={0.1}
            onChange={depthMm => updateHandle({ depthMm })}
          />
          {geometry.variant !== 'full-edge' && (
            <>
              <RangeField
                {...activationProps('handle-transition')}
                label="Dĺžka plynulého prechodu"
                value={config.handle.transitionLengthMm}
                min={limits.handle.transitionLengthMm.min}
                max={limits.handle.transitionLengthMm.max}
                step={1}
                onChange={transitionLengthMm => updateHandle({ transitionLengthMm })}
              />
              <RangeField
                {...activationProps('handle-roundness')}
                label="Plynulosť nábehu"
                value={config.handle.transitionRoundness}
                min={limits.handle.transitionRoundness.min}
                max={limits.handle.transitionRoundness.max}
                step={1}
                unit="%"
                onChange={transitionRoundness => updateHandle({ transitionRoundness })}
              />
            </>
          )}
          <RangeField
            {...activationProps('handle-profile-width')}
            label="Návrhová šírka profilu"
            value={config.handle.grooveWidthMm}
            min={limits.handle.grooveWidthMm.min}
            max={limits.handle.grooveWidthMm.max}
            step={1}
            onChange={grooveWidthMm => updateHandle({ grooveWidthMm })}
          />
          <RangeField
            {...activationProps('first-cnc-pass')}
            label="Hĺbka prvého CNC prechodu"
            value={firstCncPassDepthMm}
            min={firstCncPassMinMm}
            max={firstCncPassMaxMm}
            step={0.1}
            onChange={firstPassDepthMm =>
              updateHandle({
                roughingAllowanceMm: Number((config.handle.depthMm - firstPassDepthMm).toFixed(1)),
              })
            }
          />
          <div className={styles.cncPassHint}>
            <strong>
              1. prechod {formatNumber(firstCncPassDepthMm)} mm
              <span aria-hidden="true">→</span>
              finále {formatNumber(config.handle.depthMm)} mm
            </strong>
            <small>Technologický údaj pre CNC dáta. Hotový 3D tvar zostáva rovnaký.</small>
          </div>
        </section>

        <section className={styles.controlSection}>
          <div className={styles.sectionHeading}>
            <div>
              <span>Opracovanie hrán</span>
              <small>Samostatný modul pre ostrú, skosenú alebo zaoblenú obvodovú hranu</small>
            </div>
            <span className={styles.sectionNumber}>04</span>
          </div>
          <div className={styles.cutGrid}>
            {[
              ['square', 'Ostrá'],
              ['chamfer', 'Skosená'],
              ['roundover', 'Zaoblená'],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={
                  config.edgeTreatment.profile === value ? styles.cutButtonActive : styles.cutButton
                }
                aria-pressed={config.edgeTreatment.profile === value}
                onClick={() =>
                  updateEdges({ profile: value as ConfiguratorState['edgeTreatment']['profile'] })
                }
              >
                <EdgeTreatmentIcon
                  profile={value as ConfiguratorState['edgeTreatment']['profile']}
                />
                {label}
              </button>
            ))}
          </div>
          {config.edgeTreatment.profile !== 'square' && (
            <div className={styles.cutSettings}>
              <RangeField
                {...activationProps('edge-size')}
                label={
                  config.edgeTreatment.profile === 'chamfer' ? 'Veľkosť skosenia' : 'Rádius hrany'
                }
                value={config.edgeTreatment.sizeMm}
                min={limits.edgeTreatment.sizeMm.min}
                max={limits.edgeTreatment.sizeMm.max}
                step={0.5}
                onChange={sizeMm => updateEdges({ sizeMm })}
              />
            </div>
          )}
          <RangeField
            {...activationProps('corner-radius')}
            label="Rádius rohov v pôdoryse"
            value={config.edgeTreatment.cornerRadiusMm}
            min={limits.edgeTreatment.cornerRadiusMm.min}
            max={limits.edgeTreatment.cornerRadiusMm.max}
            step={0.5}
            onChange={cornerRadiusMm => updateEdges({ cornerRadiusMm })}
          />
          <div className={styles.stockAllowanceBox}>
            <div>
              <strong>Polotovar pre TCN</strong>
              <small>Výkres má 426 mm, zdrojový program DL=429 mm.</small>
            </div>
            <div className={styles.stockAllowanceInputs}>
              <NumberField
                {...measurementProps('stock-width')}
                label="Prídavok X"
                value={config.stockAllowanceMm.widthMm}
                min={limits.stockAllowanceMm.widthMm.min}
                max={limits.stockAllowanceMm.widthMm.max}
                step={0.5}
                onChange={widthMm => updateStock({ widthMm })}
              />
              <NumberField
                {...measurementProps('stock-height')}
                label="Prídavok Y"
                value={config.stockAllowanceMm.heightMm}
                min={limits.stockAllowanceMm.heightMm.min}
                max={limits.stockAllowanceMm.heightMm.max}
                step={0.5}
                onChange={heightMm => updateStock({ heightMm })}
              />
              <NumberField
                {...measurementProps('stock-thickness')}
                label="Prídavok Z"
                value={config.stockAllowanceMm.thicknessMm}
                min={limits.stockAllowanceMm.thicknessMm.min}
                max={limits.stockAllowanceMm.thicknessMm.max}
                step={0.5}
                onChange={thicknessMm => updateStock({ thicknessMm })}
              />
            </div>
            <span>
              Výsledný polotovar: {formatNumber(geometry.stock.widthMm)} ×{' '}
              {formatNumber(geometry.stock.heightMm)} × {formatNumber(geometry.stock.thicknessMm)}{' '}
              mm
            </span>
          </div>

          {errors.length > 0 && (
            <div className={styles.validationError}>
              <AlertTriangle size={18} />
              <div>
                <strong>Geometriu treba upraviť</strong>
                {errors.map(issue => (
                  <small key={issue.code}>{issue.message}</small>
                ))}
              </div>
            </div>
          )}
        </section>

        <section className={styles.controlSection}>
          <div className={styles.sectionHeading}>
            <div>
              <span>Povrch</span>
              <small>Dokončenie po obrábaní</small>
            </div>
            <span className={styles.sectionNumber}>05</span>
          </div>
          <div className={styles.finishList}>
            {FINISHES.map(finish => (
              <button
                key={finish.id}
                type="button"
                className={config.finishId === finish.id ? styles.finishActive : styles.finish}
                aria-pressed={config.finishId === finish.id}
                onClick={() => update({ finishId: finish.id })}
              >
                <i />
                <span>
                  <strong>{finish.label}</strong>
                  <small>{finish.description}</small>
                </span>
                {config.finishId === finish.id && <Check size={16} />}
              </button>
            ))}
          </div>
        </section>
      </div>

      <div className={styles.previewColumn}>
        <ConfiguratorPreview3D
          config={config}
          estimate={estimate}
          mode={previewMode}
          onModeChange={setPreviewMode}
          dimensionView={<TechnicalDrawing config={config} activeMeasurement={activeMeasurement} />}
        />
      </div>
    </div>
  );
}

export function DocumentsStep({
  config,
  estimate,
  outputs,
  setOutputs,
}: {
  config: ConfiguratorState;
  estimate: PriceEstimate;
  outputs: GeneratedOutputs;
  setOutputs: (updater: (previous: GeneratedOutputs) => GeneratedOutputs) => void;
}) {
  const [downloading, setDownloading] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const fingerprint = buildConfigurationFingerprint(config);
  const tcn = useMemo(() => buildTcnArtifact(config), [config]);
  const quoteReady = isOutputCurrent(outputs.quote, fingerprint);
  const drawingReady = isOutputCurrent(outputs.drawing, fingerprint);
  const tcnReady = isOutputCurrent(outputs.tcn, fingerprint);

  const download = async (key: 'quote' | 'drawing' | 'model' | 'tcn') => {
    setDownloading(key);
    setDownloadError(null);

    try {
      const response = await fetch(`/api/configurator/documents/${key}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ configuration: config }),
      });

      if (!response.ok) {
        throw new Error(`Server vr\u00e1til chybu ${response.status}.`);
      }

      const blob = await response.blob();
      if (blob.size === 0) {
        throw new Error('Server vr\u00e1til pr\u00e1zdny dokument.');
      }

      downloadBlob(
        blob,
        getResponseFilename(
          response.headers.get('Content-Disposition'),
          key === 'tcn' ? tcn.filename : `bdv-${key}.pdf`,
        ),
      );
      if (key === 'tcn') {
        setOutputs(previous => ({
          ...previous,
          tcn: createOutputArtifact(
            fingerprint,
            tcn.releaseState === 'reference-match-review-required'
              ? 'reference-program-review'
              : 'parametric-program-review',
            {
              containsMachining: tcn.containsMachining,
              releaseState: tcn.releaseState,
            },
          ),
        }));
      } else if (key !== 'model') {
        setOutputs(previous => ({
          ...previous,
          [key]: createOutputArtifact(fingerprint, 'downloaded'),
        }));
      }
    } catch (error) {
      const serverMessage =
        error instanceof Error && error.message.startsWith('Server ') ? error.message : null;
      setDownloadError(
        serverMessage ?? 'S\u00fabor sa nepodarilo stiahnu\u0165. Sk\u00faste to znova.',
      );
    } finally {
      setDownloading(null);
    }
  };

  return (
    <StepFrame
      eyebrow="04 / Dokumenty"
      title="Dokumenty a TCN"
      description="Z konfiguračných dát sa generujú štyri výstupy na priame stiahnutie: výrobný výkres, 3D model, TCN a cenová ponuka."
      wide
    >
      {downloadError && (
        <div role="alert" className={styles.statusWarning}>
          <AlertTriangle size={17} />
          <span>{downloadError}</span>
        </div>
      )}
      <div className={styles.documentsGrid}>
        <div className={styles.drawingCard}>
          <div className={styles.outputCardHeader}>
            <div className={styles.outputIcon}>
              <DraftingCompass size={22} />
            </div>
            <div>
              <span className={styles.eyebrow}>BDV-DWG-001</span>
              <h3>Výrobný výkres</h3>
            </div>
            <span className={drawingReady ? styles.readyBadge : styles.draftBadge}>
              {drawingReady ? 'Pripravené' : 'Náhľad'}
            </span>
          </div>
          <TechnicalDrawing config={config} />
          <div className={styles.outputActions}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => download('drawing')}
              disabled={downloading !== null}
            >
              <Download size={17} />
              {downloading === 'drawing' ? 'Sťahujem…' : 'Výrobný výkres'}
            </button>
            <button
              type="button"
              className={styles.ghostButton}
              onClick={() => download('model')}
              disabled={downloading !== null}
            >
              <Download size={17} />
              {downloading === 'model' ? 'Sťahujem…' : '3D model'}
            </button>
            <button
              type="button"
              className={styles.ghostButton}
              onClick={() => download('tcn')}
              disabled={downloading !== null || tcn.releaseState === 'invalid-configuration'}
              title="TCN je určený na CAM kontrolu pred spustením výroby"
            >
              <Download size={17} />
              {downloading === 'tcn'
                ? 'Sťahujem…'
                : tcnReady
                  ? 'Stiahnuť TCN znova'
                  : 'Stiahnuť TCN'}
            </button>
          </div>
        </div>

        <div className={styles.quoteCard}>
          <div className={styles.outputCardHeader}>
            <div className={styles.outputIconWarm}>
              <CircleDollarSign size={22} />
            </div>
            <div>
              <span className={styles.eyebrow}>Kalkulácia</span>
              <h3>Cenová ponuka</h3>
            </div>
            <span className={quoteReady ? styles.readyBadge : styles.draftBadge}>
              {quoteReady ? 'Pripravené' : 'Orientačná'}
            </span>
          </div>

          <div className={styles.quoteProduct}>
            <div className={styles.quoteProductPreview}>
              <GeneratedDoorPreview
                config={config}
                presentation="card"
                className={styles.quoteProductGeneratedPreview}
              />
            </div>
            <div>
              <strong>{getProduct(config.productId).label}</strong>
              <span>
                {config.quantity} ks · {getMaterial(config.materialId).label} · {config.widthMm} ×{' '}
                {config.heightMm} mm
              </span>
            </div>
          </div>

          <dl className={styles.priceBreakdown}>
            <div>
              <dt>Materiál vrátane odpadu</dt>
              <dd>{formatCurrency(estimate.material)}</dd>
            </div>
            <div>
              <dt>CNC obrábanie</dt>
              <dd>{formatCurrency(estimate.machining)}</dd>
            </div>
            <div>
              <dt>Príprava zákazky (raz)</dt>
              <dd>{formatCurrency(estimate.setup)}</dd>
            </div>
            <div>
              <dt>Povrchová úprava</dt>
              <dd>{formatCurrency(estimate.finishing)}</dd>
            </div>
            <div>
              <dt>Medzisúčet bez DPH</dt>
              <dd>{formatCurrency(estimate.subtotal)}</dd>
            </div>
            <div>
              <dt>DPH ({Math.round(estimate.vatRate * 100)} %)</dt>
              <dd>{formatCurrency(estimate.vat)}</dd>
            </div>
            {config.quantity > 1 && (
              <div>
                <dt>Cena za kus s DPH</dt>
                <dd>{formatCurrency(estimate.unitPrice)}</dd>
              </div>
            )}
            <div className={styles.priceTotal}>
              <dt>Objednávka spolu s DPH</dt>
              <dd>{formatCurrency(estimate.total)}</dd>
            </div>
          </dl>

          <div className={styles.leadTime}>
            <TimerReset size={18} />
            <span>
              Odhad dodania <strong>{estimate.leadTime}</strong>
            </span>
          </div>

          <button
            type="button"
            className={styles.primaryButtonWide}
            onClick={() => download('quote')}
            disabled={downloading !== null}
          >
            <Download size={18} />
            {downloading === 'quote' ? 'Sťahujem…' : 'Cenová ponuka'}
          </button>
          <p className={styles.disclaimer}>
            Kalkulácia predpokladá pripravený výrobný program a nezahŕňa dopravu, kovanie ani
            montáž. Finálna cena sa potvrdí po technologickej kontrole a zmeraní CNC cyklu.
          </p>
        </div>
      </div>
    </StepFrame>
  );
}

function getResponseFilename(contentDisposition: string | null, fallback: string) {
  if (!contentDisposition) return fallback;

  const encodedMatch = contentDisposition.match(/filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i);
  if (encodedMatch?.[1]) {
    try {
      return sanitizeDownloadFilename(
        decodeURIComponent(encodedMatch[1].trim().replace(/^"|"$/g, '')),
        fallback,
      );
    } catch {
      // Fall through to the plain filename value.
    }
  }

  const filenameMatch = contentDisposition.match(/filename\s*=\s*(?:"([^"]+)"|([^;]+))/i);
  const filename = filenameMatch?.[1] ?? filenameMatch?.[2];
  return filename ? sanitizeDownloadFilename(filename.trim(), fallback) : fallback;
}

function sanitizeDownloadFilename(filename: string, fallback: string) {
  const safe = filename.replace(/[\\/\u0000-\u001f\u007f]/g, '_').trim();
  return safe || fallback;
}

function downloadBlob(blob: Blob, filename: string) {
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = filename;
  document.body.appendChild(anchor);
  try {
    anchor.click();
  } finally {
    anchor.remove();
    URL.revokeObjectURL(href);
  }
}

export function SendStep({
  config,
  update,
  estimate,
  user,
}: {
  config: ConfiguratorState;
  update: UpdateConfig;
  estimate: PriceEstimate;
  user: ConfiguratorUser | null;
}) {
  return (
    <StepFrame
      eyebrow="05 / Odoslanie"
      title="Skontrolovať a odoslať"
      description="Posledná kontrola kontaktu a základných výrobných parametrov pred uložením konfigurácie."
      wide
    >
      <div className={styles.sendGrid}>
        <div className={styles.contactCard}>
          <div className={styles.cardSectionTitle}>
            <UserRound size={20} />
            <div>
              <h3>Kontaktné údaje</h3>
              <p>Na tento kontakt príde potvrdenie a ďalšia komunikácia.</p>
            </div>
          </div>
          {user && (
            <div className={styles.userContext}>
              <BadgeCheck size={17} />
              Pokračujete ako {user.mode === 'account' ? 'prihlásený používateľ' : 'hosť'}
            </div>
          )}
          <div className={styles.formGrid}>
            <label className={styles.field}>
              <span>Meno a priezvisko *</span>
              <input
                value={config.customerName}
                onChange={event => update({ customerName: event.target.value })}
                placeholder="Ján Novák"
              />
            </label>
            <label className={styles.field}>
              <span>E-mail *</span>
              <input
                type="email"
                value={config.customerEmail}
                onChange={event => update({ customerEmail: event.target.value })}
                placeholder="jan@firma.sk"
              />
            </label>
            <label className={styles.field}>
              <span>Firma</span>
              <input
                value={config.company}
                onChange={event => update({ company: event.target.value })}
                placeholder="Firma s.r.o."
              />
            </label>
            <label className={styles.field}>
              <span>Telefón</span>
              <input
                value={config.phone}
                onChange={event => update({ phone: event.target.value })}
                placeholder="+421 900 000 000"
              />
            </label>
            <label className={`${styles.field} ${styles.fieldFull}`}>
              <span>Poznámka k výrobe</span>
              <textarea
                value={config.note}
                onChange={event => update({ note: event.target.value })}
                placeholder="Doplňte termín, spôsob montáže alebo inú dôležitú informáciu…"
                rows={4}
              />
            </label>
          </div>
          <label className={styles.consent}>
            <input
              type="checkbox"
              checked={config.consent}
              onChange={event => update({ consent: event.target.checked })}
            />
            <span>
              Súhlasím so spracovaním údajov na účely posúdenia a spracovania tejto konfigurácie.
            </span>
          </label>
        </div>

        <aside className={styles.finalSummary}>
          <span className={styles.eyebrowLight}>Súhrn zákazky</span>
          <h3>{getProduct(config.productId).label}</h3>
          <p>
            {getMaterial(config.materialId).label} · {getMachine(config.machineId).label}
          </p>
          <div className={styles.finalPartVisual}>
            <GeneratedDoorPreview
              config={config}
              presentation="card"
              className={styles.finalPartGeneratedPreview}
            />
            <span>{config.quantity} ks</span>
          </div>
          <dl>
            <div>
              <dt>Rozmer</dt>
              <dd>
                {config.widthMm} × {config.heightMm} × {config.thicknessMm} mm
              </dd>
            </div>
            <div>
              <dt>Výstupy</dt>
              <dd>Výkres · 3D model · TCN · ponuka</dd>
            </div>
            <div>
              <dt>Dodanie</dt>
              <dd>{estimate.leadTime}</dd>
            </div>
          </dl>
          <div className={styles.finalPrice}>
            <span>Orientačná cena objednávky s DPH</span>
            <strong>{formatCurrency(estimate.total)}</strong>
            {config.quantity > 1 && <small>{formatCurrency(estimate.unitPrice)} / ks s DPH</small>}
          </div>
          <div className={styles.secureSend}>
            <ShieldCheck size={18} />
            Dáta sa odošlú až po vašom potvrdení
          </div>
        </aside>
      </div>
    </StepFrame>
  );
}

export function SuccessScreen({
  config,
  estimate,
  reference,
  onNew,
}: {
  config: ConfiguratorState;
  estimate: PriceEstimate;
  reference: string;
  onNew: () => void;
}) {
  const { t } = useTranslations();

  return (
    <div className={styles.successPage}>
      <div className={styles.successAmbient} />
      <div className={styles.successCard}>
        <div className={styles.successIcon}>
          <PackageCheck size={34} />
        </div>
        <span className={styles.eyebrow}>Konfigurácia odoslaná</span>
        <h1>Hotovo. Výrobný návrh je na ceste k nám.</h1>
        <p>
          Potvrdenie sme pripravili pre <strong>{config.customerEmail}</strong>. Pred výrobou ešte
          skontrolujeme geometriu, stroj a finálnu kalkuláciu.
        </p>
        <div className={styles.referenceBox}>
          <span>Referenčné číslo</span>
          <strong>{reference}</strong>
          <button
            type="button"
            onClick={() => navigator.clipboard?.writeText(reference)}
            aria-label="Kopírovať referenčné číslo"
          >
            <Clipboard size={16} />
          </button>
        </div>
        <div className={styles.successStats}>
          <div>
            <Ruler size={18} />
            <span>Výrobok</span>
            <strong>{getProduct(config.productId).label}</strong>
          </div>
          <div>
            <Gauge size={18} />
            <span>Odhad ceny</span>
            <strong>{formatCurrency(estimate.total)}</strong>
          </div>
          <div>
            <TimerReset size={18} />
            <span>Ďalší krok</span>
            <strong>Technologická kontrola</strong>
          </div>
        </div>
        <div className={styles.successActions}>
          <button type="button" className={styles.primaryButton} onClick={onNew}>
            <Sparkles size={17} /> {t('text4066')}
          </button>
          <a className={styles.secondaryButton} href="mailto:info@bdvtrade.sk">
            <Mail size={17} /> Kontaktovať BDV
          </a>
        </div>
      </div>
    </div>
  );
}

function TechnicalDrawing({
  config,
  activeMeasurement,
}: {
  config: ConfiguratorState;
  activeMeasurement?: ConfiguratorMeasurementId;
}) {
  const drawingId = useId().replaceAll(':', '');
  const gridId = `drawing-grid-${drawingId}`;
  const arrowId = `drawing-arrow-${drawingId}`;
  const activeArrowId = `drawing-arrow-active-${drawingId}`;
  const geometry = deriveDoorGeometry(config);
  const maxPanelWidth = 300;
  const maxPanelHeight = 205;
  const drawingScale = Math.min(
    maxPanelWidth / geometry.finished.widthMm,
    maxPanelHeight / geometry.finished.heightMm,
  );
  const panelWidth = geometry.finished.widthMm * drawingScale;
  const panelHeight = geometry.finished.heightMm * drawingScale;
  const panelOriginX = 105 + (maxPanelWidth - panelWidth) / 2;
  const panelOriginY = 72 + (maxPanelHeight - panelHeight) / 2;
  const startX = geometry.handle.startMm * drawingScale;
  const endX = geometry.handle.endMm * drawingScale;
  const drop = geometry.handle.faceWidthMm * drawingScale;
  const transition = geometry.handle.transitionLengthMm * drawingScale;
  const transitionStartX = geometry.handle.capLeft ? startX : endX - transition;
  const transitionEndX = geometry.handle.capLeft ? startX + transition : endX;
  const transitionDefinition = buildHandleTransitionCurve(
    { x: 0, y: 0 },
    { x: geometry.handle.transitionLengthMm, y: -geometry.handle.faceWidthMm },
    geometry.handle.transitionRoundness,
  );
  const transitionControlOffsetMm = transitionDefinition.startControl.x;
  const groovePath = buildDrawingGroovePath(geometry, panelWidth, startX, endX, drop, transition);
  const pocketPath = buildDrawingPocketPath(geometry, groovePath, startX);
  const hiddenProfileOffset = Math.max(
    3,
    Math.min(12, geometry.handle.grooveWidthMm * drawingScale * 0.85),
  );
  const thicknessView = 42;
  const depthView = (geometry.handle.depthMm / geometry.finished.thicknessMm) * thicknessView;
  const firstPassDepthMm = Math.max(0, geometry.handle.depthMm - config.handle.roughingAllowanceMm);
  const firstPassView = (firstPassDepthMm / geometry.finished.thicknessMm) * thicknessView;
  const sectionProfileScale = Math.min(4.5, 58 / Math.max(geometry.handle.grooveWidthMm, 1));
  const sectionCenterY = 44;
  const depthDimensionY = sectionCenterY;
  const sectionProfile = createHandleProfile(
    geometry.handle.grooveWidthMm,
    geometry.handle.depthMm,
    0,
    0,
    20,
  );
  const visibleSectionProfile = sectionProfile.slice(0, -2);
  const sectionPoint = (point: { x: number; y: number }) => ({
    x: Math.max(0, -point.y * (thicknessView / geometry.finished.thicknessMm)),
    y: sectionCenterY - point.x * sectionProfileScale,
  });
  const sectionCurvePath = visibleSectionProfile
    .map((point, index) => {
      const mapped = sectionPoint(point);
      return `${index === 0 ? 'M' : 'L'}${mapped.x} ${mapped.y}`;
    })
    .join(' ');
  const sectionVoidPath = `${sectionCurvePath} L${depthView} 0 L0 0 Z`;
  const sectionBoundaryPath = `${sectionCurvePath} L${depthView} 0`;
  const dimensionStroke = (id: ConfiguratorMeasurementId) =>
    activeMeasurement === id ? '#c67b36' : '#4c5b53';
  const dimensionMarker = (id: ConfiguratorMeasurementId) =>
    `url(#${activeMeasurement === id ? activeArrowId : arrowId})`;
  const dimensionStrokeWidth = 1;
  const endpointLabels = (
    id: ConfiguratorMeasurementId,
    fromX: number,
    fromY: number,
    toX: number,
    toY: number,
  ) =>
    activeMeasurement === id ? (
      <g aria-hidden="true">
        <g transform={`translate(${fromX} ${fromY})`}>
          <circle r="8" fill="#c67b36" />
          <text y="3.2" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="800">
            A
          </text>
        </g>
        <g transform={`translate(${toX} ${toY})`}>
          <circle r="8" fill="#c67b36" />
          <text y="3.2" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="800">
            B
          </text>
        </g>
      </g>
    ) : null;
  return (
    <div className={styles.technicalDrawing}>
      <svg viewBox="0 0 660 370" role="img" aria-label="Parametrická technická schéma dvierok">
        <defs>
          <pattern id={gridId} width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#dfe4e0" strokeWidth="0.7" />
          </pattern>
          <marker
            id={arrowId}
            markerWidth="7"
            markerHeight="7"
            refX="7"
            refY="3.5"
            markerUnits="userSpaceOnUse"
            orient="auto-start-reverse"
          >
            <path d="M0,0 L7,3.5 L0,7 z" fill="#4c5b53" />
          </marker>
          <marker
            id={activeArrowId}
            markerWidth="7"
            markerHeight="7"
            refX="7"
            refY="3.5"
            markerUnits="userSpaceOnUse"
            orient="auto-start-reverse"
          >
            <path d="M0,0 L7,3.5 L0,7 z" fill="#c67b36" />
          </marker>
        </defs>
        <rect width="660" height="370" fill={`url(#${gridId})`} />
        <g transform={`translate(${panelOriginX} ${panelOriginY})`}>
          <rect
            x="0"
            y="0"
            width={panelWidth}
            height={panelHeight}
            rx={geometry.edge.cornerRadiusMm * drawingScale}
            fill="rgba(154,104,64,.08)"
            stroke="#25372e"
            strokeWidth="2"
          />
          <path d={pocketPath} fill="rgba(95, 57, 34, .14)" stroke="none" />
          <path
            d={groovePath}
            fill="none"
            stroke="#25372e"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d={groovePath}
            fill="none"
            stroke="#65756d"
            strokeWidth="1.2"
            strokeDasharray="6 5"
            transform={`translate(0 ${hiddenProfileOffset})`}
          />

          <line
            x1="0"
            y1={panelHeight + 28}
            x2={panelWidth}
            y2={panelHeight + 28}
            stroke={dimensionStroke('finished-width')}
            strokeWidth={dimensionStrokeWidth}
            markerStart={dimensionMarker('finished-width')}
            markerEnd={dimensionMarker('finished-width')}
          />
          <line x1="0" y1={panelHeight + 7} x2="0" y2={panelHeight + 39} stroke="#87928c" />
          <line
            x1={panelWidth}
            y1={panelHeight + 7}
            x2={panelWidth}
            y2={panelHeight + 39}
            stroke="#87928c"
          />
          <text
            x={panelWidth / 2}
            y={panelHeight + 50}
            textAnchor="middle"
            fill="#26372f"
            fontSize="14"
          >
            {formatNumber(geometry.finished.widthMm)} mm
          </text>
          {endpointLabels('finished-width', 0, panelHeight + 28, panelWidth, panelHeight + 28)}
          <line
            x1="-30"
            y1="0"
            x2="-30"
            y2={panelHeight}
            stroke={dimensionStroke('finished-height')}
            strokeWidth={dimensionStrokeWidth}
            markerStart={dimensionMarker('finished-height')}
            markerEnd={dimensionMarker('finished-height')}
          />
          <line x1="-38" y1="0" x2="-2" y2="0" stroke="#87928c" />
          <line x1="-38" y1={panelHeight} x2="-2" y2={panelHeight} stroke="#87928c" />
          <text
            x="-47"
            y={panelHeight / 2}
            textAnchor="middle"
            fill="#26372f"
            fontSize="14"
            transform={`rotate(-90 -47 ${panelHeight / 2})`}
          >
            {formatNumber(geometry.finished.heightMm)} mm
          </text>
          {endpointLabels('finished-height', -30, 0, -30, panelHeight)}

          <line
            x1={startX}
            y1="-28"
            x2={endX}
            y2="-28"
            stroke={dimensionStroke('handle-length')}
            strokeWidth={dimensionStrokeWidth}
            markerStart={dimensionMarker('handle-length')}
            markerEnd={dimensionMarker('handle-length')}
          />
          <line x1={startX} y1="-35" x2={startX} y2="3" stroke="#87928c" />
          <line x1={endX} y1="-35" x2={endX} y2="3" stroke="#87928c" />
          <text x={(startX + endX) / 2} y="-39" textAnchor="middle" fill="#26372f" fontSize="13">
            úchyt {formatNumber(geometry.handle.lengthMm)} mm
          </text>
          {endpointLabels('handle-length', startX, -28, endX, -28)}

          {activeMeasurement === 'handle-offset' && (
            <g>
              <line
                x1="0"
                y1="-10"
                x2={startX}
                y2="-10"
                stroke={dimensionStroke('handle-offset')}
                strokeWidth={dimensionStrokeWidth}
                markerStart={dimensionMarker('handle-offset')}
                markerEnd={dimensionMarker('handle-offset')}
              />
              <line x1="0" y1="-17" x2="0" y2="3" stroke="#87928c" />
              <line x1={startX} y1="-17" x2={startX} y2="3" stroke="#87928c" />
              <text
                x={startX / 2}
                y="5"
                textAnchor="middle"
                fill="#9a5925"
                fontSize="9"
                fontWeight="700"
              >
                odsadenie {formatNumber(geometry.handle.offsetMm)} mm
              </text>
              {endpointLabels('handle-offset', 0, -10, startX, -10)}
            </g>
          )}

          {(activeMeasurement === 'handle-transition' ||
            activeMeasurement === 'handle-roundness') && (
            <g>
              <line
                x1={transitionStartX}
                y1={drop + 14}
                x2={transitionEndX}
                y2={drop + 14}
                stroke="#c67b36"
                strokeWidth={dimensionStrokeWidth}
                markerStart={`url(#${activeArrowId})`}
                markerEnd={`url(#${activeArrowId})`}
              />
              <line
                x1={transitionStartX}
                y1="0"
                x2={transitionStartX}
                y2={drop + 21}
                stroke="#b88659"
                strokeDasharray="3 3"
              />
              <line
                x1={transitionEndX}
                y1="0"
                x2={transitionEndX}
                y2={drop + 21}
                stroke="#b88659"
                strokeDasharray="3 3"
              />
              <text
                x={(transitionStartX + transitionEndX) / 2}
                y={drop + 29}
                textAnchor="middle"
                fill="#9a5925"
                fontSize="9"
                fontWeight="700"
              >
                prechod {formatNumber(geometry.handle.transitionLengthMm)} mm
              </text>
              {activeMeasurement === 'handle-transition' &&
                endpointLabels(
                  'handle-transition',
                  transitionStartX,
                  drop + 14,
                  transitionEndX,
                  drop + 14,
                )}
            </g>
          )}

          <line
            x1={panelWidth + 22}
            y1="0"
            x2={panelWidth + 22}
            y2={drop}
            stroke={dimensionStroke('handle-face-width')}
            strokeWidth={dimensionStrokeWidth}
            markerStart={dimensionMarker('handle-face-width')}
            markerEnd={dimensionMarker('handle-face-width')}
          />
          <text x={panelWidth + 31} y={drop / 2 + 4} fill="#26372f" fontSize="12">
            {formatNumber(geometry.handle.faceWidthMm)}
          </text>
          {endpointLabels('handle-face-width', panelWidth + 22, 0, panelWidth + 22, drop)}
          <text x="4" y={panelHeight - 8} fill="#65756d" fontSize="10">
            R{formatNumber(geometry.edge.cornerRadiusMm)} ·{' '}
            {getEdgeLabel(config.edgeTreatment.profile)}
          </text>
          <text x="4" y={panelHeight + 68} fill="#65756d" fontSize="9">
            B5 prechod L={formatNumber(geometry.handle.transitionLengthMm)} mm · a=
            {formatNumber(transitionControlOffsetMm)} mm · profil G=
            {formatNumber(geometry.handle.grooveWidthMm)} mm
          </text>
        </g>

        <g transform="translate(500 88)">
          <text x="55" y="-22" textAnchor="middle" fill="#65756d" fontSize="10">
            REZ ÚCHYTKOU Y–Q · SCHEMATICKÝ DETAIL
          </text>
          <rect
            x="0"
            y="0"
            width={thicknessView}
            height="148"
            fill="rgba(154,104,64,.12)"
            stroke="#25372e"
            strokeWidth="2"
          />
          <path d={sectionVoidPath} fill="#f8faf8" stroke="none" />
          <line x1="0" y1="0" x2={depthView} y2="0" stroke="#f8faf8" strokeWidth="3" />
          <line x1="0" y1="0" x2="0" y2={sectionCenterY} stroke="#f8faf8" strokeWidth="3" />
          <path
            d={sectionBoundaryPath}
            fill="none"
            stroke="#9a6840"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <line
            x1="0"
            y1={depthDimensionY}
            x2={depthView}
            y2={depthDimensionY}
            stroke={dimensionStroke('handle-depth')}
            strokeWidth={dimensionStrokeWidth}
            markerStart={dimensionMarker('handle-depth')}
            markerEnd={dimensionMarker('handle-depth')}
          />
          <text
            x={thicknessView + 8}
            y={depthDimensionY - 5}
            textAnchor="start"
            fill="#26372f"
            fontSize="10.5"
            fontWeight="700"
          >
            Q {formatNumber(geometry.handle.depthMm)} mm
          </text>
          {endpointLabels('handle-depth', 0, depthDimensionY, depthView, depthDimensionY)}
          {activeMeasurement === 'first-cnc-pass' && (
            <g>
              <line
                x1="0"
                y1="112"
                x2={firstPassView}
                y2="112"
                stroke={dimensionStroke('first-cnc-pass')}
                strokeWidth={dimensionStrokeWidth}
                markerStart={dimensionMarker('first-cnc-pass')}
                markerEnd={dimensionMarker('first-cnc-pass')}
              />
              <line
                x1={firstPassView}
                y1="0"
                x2={firstPassView}
                y2={sectionCenterY}
                stroke="#b88659"
                strokeDasharray="3 3"
              />
              <text
                x={firstPassView / 2}
                y="128"
                textAnchor="middle"
                fill="#9a5925"
                fontSize="9"
                fontWeight="700"
              >
                1. prechod {formatNumber(firstPassDepthMm)}
              </text>
              {endpointLabels('first-cnc-pass', 0, 112, firstPassView, 112)}
            </g>
          )}
          <line
            x1="0"
            y1="174"
            x2={thicknessView}
            y2="174"
            stroke={dimensionStroke('finished-thickness')}
            strokeWidth={dimensionStrokeWidth}
            markerStart={dimensionMarker('finished-thickness')}
            markerEnd={dimensionMarker('finished-thickness')}
          />
          <text x={thicknessView / 2} y="194" textAnchor="middle" fill="#26372f" fontSize="12">
            {formatNumber(geometry.finished.thicknessMm)} mm
          </text>
          {endpointLabels('finished-thickness', 0, 174, thicknessView, 174)}
          <text x="0" y="218" fill="#65756d" fontSize="9">
            Q = hĺbka frézovania
          </text>
          <text x="0" y="234" fill="#9a5925" fontSize="8.5">
            Tvar podľa 3D náhľadu · profil nástroja 1009 overiť
          </text>
        </g>
        <text x="18" y="24" fill="#65756d" fontSize="11">
          POHĽAD SPREDU · PARAMETRICKÝ NÁHĽAD
        </text>
        <text x="477" y="346" fill="#65756d" fontSize="10">
          BDV TRADE · TECH REVIEW
        </text>
      </svg>
    </div>
  );
}

function buildDrawingGroovePath(
  geometry: ReturnType<typeof deriveDoorGeometry>,
  panelWidth: number,
  startX: number,
  endX: number,
  drop: number,
  transition: number,
) {
  if (geometry.variant === 'full-edge') return `M 0 ${drop} H ${panelWidth}`;

  const left = sampleHandleTransition(
    buildHandleTransitionCurve(
      { x: startX, y: 0 },
      { x: startX + transition, y: drop },
      geometry.handle.transitionRoundness,
    ),
  );
  const right = sampleHandleTransition(
    buildHandleTransitionCurve(
      { x: endX - transition, y: drop },
      { x: endX, y: 0 },
      geometry.handle.transitionRoundness,
    ),
  );
  const leftCurve = left
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ');
  const rightCurve = right
    .slice(1)
    .map(point => `L ${point.x} ${point.y}`)
    .join(' ');
  if (geometry.variant === 'double-terminated') {
    return `${leftCurve} H ${endX - transition} ${rightCurve}`;
  }
  if (geometry.handle.openRight) return `${leftCurve} H ${panelWidth}`;
  return `M 0 ${drop} H ${endX - transition} ${rightCurve}`;
}

function buildDrawingPocketPath(
  geometry: ReturnType<typeof deriveDoorGeometry>,
  profilePath: string,
  startX: number,
) {
  if (geometry.variant === 'full-edge') return `${profilePath} V 0 H 0 Z`;
  if (geometry.variant === 'double-terminated') return `${profilePath} Z`;
  if (geometry.handle.openRight) return `${profilePath} V 0 H ${startX} Z`;
  return `${profilePath} H 0 Z`;
}

function getEdgeLabel(profile: ConfiguratorState['edgeTreatment']['profile']) {
  if (profile === 'chamfer') return 'skosenie';
  if (profile === 'roundover') return 'zaoblenie';
  return 'ostrá hrana';
}

function EdgeTreatmentIcon({
  profile,
}: {
  profile: ConfiguratorState['edgeTreatment']['profile'];
}) {
  return (
    <svg viewBox="0 0 28 30" aria-hidden="true" focusable="false">
      {profile === 'chamfer' ? (
        <path d="M6 1H22L27 6V24L22 29H6L1 24V6Z" />
      ) : (
        <rect x="1" y="1" width="26" height="28" rx={profile === 'roundover' ? 7 : 0} />
      )}
    </svg>
  );
}

function StepFrame({
  eyebrow,
  title,
  description,
  children,
  wide = false,
}: {
  eyebrow: string;
  title: string;
  description: string;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <div className={wide ? styles.stepFrameWide : styles.stepFrame}>
      <header className={styles.stepHeader}>
        <span className={styles.eyebrow}>{eyebrow}</span>
        <h1>{title}</h1>
        <p>{description}</p>
      </header>
      {children}
    </div>
  );
}

function NumberField({
  label,
  value,
  min,
  max,
  step = 1,
  unit = 'mm',
  onChange,
  measurement,
  active = false,
  onActivate,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  onChange: (value: number) => void;
  measurement?: ConfiguratorMeasurement;
  active?: boolean;
  onActivate?: () => void;
}) {
  return (
    <label
      className={active ? styles.compactFieldActive : styles.compactField}
      onPointerDown={onActivate}
      onFocusCapture={onActivate}
    >
      <span>{label}</span>
      <div>
        <input
          type="number"
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={event => onChange(Math.min(max, Math.max(min, Number(event.target.value))))}
        />
        <i>{unit}</i>
      </div>
      {measurement && <FieldMeasurementHint measurement={measurement} />}
    </label>
  );
}

function RangeField({
  label,
  value,
  min,
  max,
  step,
  unit = 'mm',
  onChange,
  active = false,
  onActivate,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit?: string;
  onChange: (value: number) => void;
  active?: boolean;
  onActivate?: () => void;
}) {
  const safeMax = Math.max(min, max);
  const [draftValue, setDraftValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const updateDraft = (nextDraft: string) => {
    setDraftValue(nextDraft);
    if (nextDraft.trim() === '') return;

    const nextValue = Number(nextDraft);
    if (Number.isFinite(nextValue) && nextValue >= min && nextValue <= safeMax) {
      onChange(nextValue);
    }
  };

  const commitDraft = () => {
    const nextValue = Number(draftValue);
    if (draftValue.trim() === '' || !Number.isFinite(nextValue)) {
      setDraftValue(String(value));
      setIsEditing(false);
      return;
    }

    const clampedValue = Math.min(safeMax, Math.max(min, nextValue));
    onChange(clampedValue);
    setDraftValue(String(clampedValue));
    setIsEditing(false);
  };

  return (
    <div
      className={active ? styles.rangeFieldActive : styles.rangeField}
      onPointerDown={onActivate}
      onFocusCapture={onActivate}
      role="group"
      aria-label={label}
    >
      <span>
        <strong>{label}</strong>
        <span className={styles.rangeValueEditor}>
          <input
            type="number"
            value={isEditing ? draftValue : String(value)}
            min={min}
            max={safeMax}
            step={step}
            inputMode="decimal"
            aria-label={`${label} – presná hodnota v ${unit}`}
            onFocus={() => {
              setDraftValue(String(value));
              setIsEditing(true);
            }}
            onChange={event => updateDraft(event.target.value)}
            onBlur={commitDraft}
            onKeyDown={event => {
              if (event.key === 'Enter') event.currentTarget.blur();
            }}
          />
          <i>{unit}</i>
        </span>
      </span>
      <input
        className={styles.rangeSlider}
        type="range"
        min={min}
        max={safeMax}
        step={step}
        value={Math.min(value, safeMax)}
        aria-label={`${label} – posuvník`}
        onChange={event => onChange(Number(event.target.value))}
      />
    </div>
  );
}

function FieldMeasurementHint({ measurement }: { measurement: ConfiguratorMeasurement }) {
  return (
    <small className={styles.fieldMeasurementHint} title={measurement.coordinateText}>
      {measurement.exactAnchors ? (
        <>
          <span>A</span>
          {measurement.from}
          <b aria-hidden="true">→</b>
          <span>B</span>
          {measurement.to}
        </>
      ) : (
        <>
          <span aria-hidden="true">i</span>
          {measurement.coordinateText}
        </>
      )}
    </small>
  );
}

function formatNumber(value: number) {
  return new Intl.NumberFormat('sk-SK', { maximumFractionDigits: 1 }).format(value);
}

export function formatCurrency(value: number) {
  return new Intl.NumberFormat('sk-SK', { style: 'currency', currency: 'EUR' }).format(value);
}
